import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { ReachStakeholder, reachStakeholdersAPI } from '../../../services/reachService';
import { StakeholderMapQuadrants } from './StakeholderMapQuadrants';
import { StakeholderMapNode } from './StakeholderMapNode';
import { StakeholderForm } from './StakeholderForm';
import { toast } from 'sonner';

interface Props {
  projectId: string;
}

export const StakeholderMap: React.FC<Props> = ({ projectId }) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [stakeholders, setStakeholders] = useState<ReachStakeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ReachStakeholder | null>(null);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  useEffect(() => {
    reachStakeholdersAPI.list(projectId)
      .then(r => setStakeholders(r.data.stakeholders))
      .catch(() => toast.error('Failed to load stakeholders'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, delta } = event;
    const canvas = canvasRef.current;
    if (!canvas) return;

    setStakeholders(prev => prev.map(s => {
      if (s.id !== active.id) return s;
      const newX = Math.max(0, Math.min(1, s.x + delta.x / canvas.offsetWidth));
      const newY = Math.max(0, Math.min(1, s.y - delta.y / canvas.offsetHeight));
      const updated = { ...s, x: newX, y: newY };

      // Debounce the API call
      clearTimeout(debounceTimers.current[s.id]);
      debounceTimers.current[s.id] = setTimeout(() => {
        reachStakeholdersAPI.updatePosition(projectId, s.id, newX, newY)
          .catch(() => toast.error('Failed to save position'));
      }, 300);

      return updated;
    }));
  }, [projectId]);

  const handleAdd = async (data: Omit<ReachStakeholder, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const res = await reachStakeholdersAPI.create(projectId, { ...data, x: 0.5, y: 0.5 });
      setStakeholders(prev => [...prev, res.data.stakeholder]);
      setShowForm(false);
      toast.success('Stakeholder added');
    } catch {
      toast.error('Failed to add stakeholder');
    }
  };

  const handleEdit = async (data: Omit<ReachStakeholder, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!editing) return;
    try {
      const res = await reachStakeholdersAPI.update(projectId, editing.id, data);
      setStakeholders(prev => prev.map(s => s.id === editing.id ? res.data.stakeholder : s));
      setEditing(null);
      toast.success('Stakeholder updated');
    } catch {
      toast.error('Failed to update stakeholder');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await reachStakeholdersAPI.delete(projectId, id);
      setStakeholders(prev => prev.filter(s => s.id !== id));
      toast.success('Stakeholder removed');
    } catch {
      toast.error('Failed to remove stakeholder');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96 text-slate-400">Loading map…</div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{stakeholders.length} stakeholder{stakeholders.length !== 1 ? 's' : ''} — drag to reposition</p>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors"
        >
          <Plus size={16} /> Add Stakeholder
        </button>
      </div>

      {/* Canvas with external axis labels */}
      <div className="flex gap-2 items-stretch">
        {/* Y axis label — outside the canvas */}
        <div className="flex items-center justify-center w-4 flex-shrink-0">
          <span className="-rotate-90 text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap select-none">
            Influence →
          </span>
        </div>

        <div className="flex-1 flex flex-col gap-1 min-w-0">
          <DndContext sensors={sensors} onDragEnd={handleDragEnd} modifiers={[restrictToParentElement]}>
            <div
              ref={canvasRef}
              className="relative bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden"
              style={{ height: 560 }}
            >
              <StakeholderMapQuadrants />
              {stakeholders.map(s => (
                <StakeholderMapNode
                  key={s.id}
                  stakeholder={s}
                  onEdit={setEditing}
                />
              ))}
              {stakeholders.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <p className="text-slate-400 text-sm font-medium">No stakeholders yet</p>
                    <p className="text-slate-300 text-xs mt-1">Click "Add Stakeholder" to get started</p>
                  </div>
                </div>
              )}
            </div>
          </DndContext>

          {/* X axis label — below the canvas */}
          <div className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest select-none">
            Readiness →
          </div>
        </div>
      </div>

      {/* Stakeholder list */}
      {stakeholders.length > 0 && (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#e2e8f0] bg-slate-50/50">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Stakeholders</p>
          </div>
          <div className="divide-y divide-[#e2e8f0]">
            {stakeholders.map(s => (
              <div key={s.id} className="flex items-center gap-3 px-5 py-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: s.color }}>
                  {s.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-navy truncate">{s.name}</p>
                  <p className="text-xs text-slate-500 truncate">{s.role}{s.department ? ` · ${s.department}` : ''}</p>
                </div>
                <span className="text-xs text-slate-400 capitalize">{s.adoptionCurve?.replace('_', ' ')}</span>
                <button onClick={() => setEditing(s)} className="text-slate-300 hover:text-primary transition-colors"><Pencil size={14} /></button>
                <button onClick={() => handleDelete(s.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {(showForm || editing) && (
        <StakeholderForm
          initial={editing ?? undefined}
          onSave={editing ? handleEdit : handleAdd}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
};
